<template>
  <div class="settings-block">
    <div class="settings-block__title">
      <div class="settings-block__title-text">{{ t('Cloaking domains') }}</div>
      <div class="settings-block__title-buttons">
        <ui-button
          planLimits="redirectDomainsAmount"
          :text="t('Add custom domain')"
          icon="fas fa-plus"
          button-style="accent"
          @click="addCustomDomain"
        />
      </div>
    </div>
    <div>
      {{
        t(
          'Here you can add your custom cloaking domains. We highly recommend adding a custom domain. The default bemobpath.com is meant for testing purposes only.',
        )
      }}
    </div>
    <ui-table
      domains-table
      :headers="headers"
      searchBy="domain"
      :sortBy="[
        {
          key: 'domain',
          order: 'asc',
        },
      ]"
      :items="store.redirectDomains.data"
      :showPagination="false"
      :useSearch="false"
      :virtualize="false"
      class="main-table"
    >
      <template #item.domain="{ item }">
        <div class="d-flex flex-wrap align-center">
          {{ item.domain }}
          <ui-chip
            class="main-chip ml-4"
            chipStyle="round"
            :contentColor="$colors.white"
            v-if="item.id === store.user?.redirectDomainId"
          >
            {{ t('Main') }}
          </ui-chip>
          <ui-chip
            :contentColor="$colors.white"
            chipStyle="round"
            class="https-chip ml-4"
            v-if="item.httpsAvailable"
          >
            <ui-icon class="mr-4" size="8" :color="$colors.white" icon="fas fa-lock" />
            HTTPS
          </ui-chip>
          <ui-chip
            :contentColor="$colors.white"
            chipStyle="round"
            class="ssl-pending ml-4"
            v-if="!item.httpsAvailable && !isNextDnsRecordValidationAfterYear(item)"
          >
            SSL Pending
          </ui-chip>
          <ui-chip
            :contentColor="$colors.white"
            chipStyle="round"
            :backgroundColor="$colors.green"
            class="ml-4"
            v-if="isNextDnsRecordValidationAfterYear(item)"
          >
            {{ t('Client-managed domain') }}
          </ui-chip>
          <ui-chip
            chipStyle="round"
            :backgroundColor="$colors.red"
            :contentColor="$colors.white"
            class="validDnsRecord-chip ml-4"
            v-if="item.validDnsRecord !== 1"
          >
            <ui-icon
              class="mr-4"
              size="8"
              :color="$colors.white"
              icon="fa fa-exclamation-triangle"
            />
            Invalid DNS record
          </ui-chip>
        </div>
      </template>
      <template #item.setMain="{ item }">
        <ui-button
          button-style="simple"
          class="set-as-main justify-end"
          :class="{
            ['set-as-main__active']: item.id !== store.user?.redirectDomainId,
          }"
          :contentColor="
            item.id !== store.user?.redirectDomainId
              ? $colors.gray20
              : $colors.graphite
          "
          :text="t('Set as main')"
          @click="item.id !== store.user?.redirectDomainId && setAsDefault(item.id)"
          :icon="item.id === store.user?.redirectDomainId && 'fas fa-check'"
        />
      </template>
      <template #item.action="{ item }">
        <ui-button
          v-if="item.custom && item.id !== store.user?.redirectDomainId"
          button-style="simple"
          class="action-button"
          :text="t('Delete')"
          @click="checkDelete(item.id)"
          :contentColor="$colors.red"
          icon="fas fa-trash-alt"
        />
      </template>
    </ui-table>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useColors, useGetUsingType, useI18n } from '@/composables';
import { useStore } from '@/store';
import { $emitter } from '@/services/emitter';
import {
  deleteRedirectDomainApiRequest,
  putUserDomainDataApiRequest,
} from '@/services/api/domains-api';
import { populateRedirectDomainsStoreCacheApiRequest } from '@/services/api/api-services/domains';
import moment from 'moment';

const { t } = useI18n();
const $colors = useColors();
const store = useStore();
const { getUsingType } = useGetUsingType();

const headers = computed(() => [
  {
    title: t('Domain'),
    align: 'left',
    value: 'domain',
    sortable: false,
  },
  {
    title: '',
    value: 'setMain',
    align: 'right',
    sortable: false,
  },
  {
    title: '',
    value: 'action',
    align: 'right',
    sortable: false,
  },
]);

const checkDelete = (id: string) => {
  $emitter.emit('open-dialog', {
    name: 'ConfirmDialog',
    data: {
      message: t('All selected items will be deleted permanently if you press OK.'),
      onConfirm: () => {
        deleteRedirectDomainApiRequest(id)
          .then(() => {
            $emitter.emit('toast-success', t('Domain deleted'));
          })
          .catch(
            (errorResponse: {
              response?: { data?: { errors?: { id?: unknown[] } } };
            }) => {
              const idErrors = errorResponse.response?.data?.errors?.id?.[0];
              if (!Array.isArray(idErrors)) return;
              for (const item of idErrors) {
                const usingType = getUsingType({ type: item.usingType });
                const message = t('{0} is used by {1} {2}', [
                  item.usedName,
                  usingType,
                  item.usingName,
                ]);
                $emitter.emit('toast-warning', message, {
                  dontHide: true,
                });
              }
            },
          );
      },
    },
  });
};

const setAsDefault = (id: string) => {
  store.setLoading(true);
  return putUserDomainDataApiRequest({ redirectDomainId: id })
    .then((res: unknown) => {
      store.mergeUser(res as unknown as Record<string, unknown>);
    })
    .finally(() => (store.setLoading(false)));
};

const addCustomDomain = () => {
  $emitter.emit('open-dialog', {
    name: 'AddCustomCloakingDomainDialog',
  });
};

const isNextDnsRecordValidationAfterYear = (item: { nextDnsRecordValidation?: string }) =>
  moment().add(1, 'year').isBefore(item.nextDnsRecordValidation);

onMounted(async () => {
  if (!store.redirectDomains.isUploaded) {
    await populateRedirectDomainsStoreCacheApiRequest();
  }
});
</script>

<style lang="scss" scoped>
@import '@/styles/pages/settings/domains';

.main-table {
  :deep(.ui-table__inner) {
    min-width: 800px;
  }
  :deep(.table-header),
  :deep(.table-row) {
    flex-grow: 1;
    .table-cell {
      flex: initial;
    }
  }
  :deep(.table-cell:nth-child(1)) {
    word-break: break-word;
    flex-grow: 1;
    flex-shrink: 1;
    min-width: 100px;
  }
  :deep(.table-cell:nth-child(2)) {
    width: 100px !important;
    flex-shrink: 0;
  }
  :deep(.table-cell:nth-child(2):lang(es)),
  :deep(.table-cell:nth-child(2):lang(ru)) {
    width: 150px !important;
  }
  :deep(.table-cell:nth-child(3)) {
    width: 55px !important;
    flex-shrink: 0;
  }
  :deep(.table-cell:nth-child(3):lang(es)),
  :deep(.table-cell:nth-child(3):lang(ru)) {
    width: 70px !important;
  }
}
</style>
